import { useCallback, useMemo } from 'react';
import { formatPriceAdjustmentDisplay } from '../../../../utils/formatters/money';
import { formatDateTime } from '../../../../utils/formatters/date';
import {
  buildPrintChromeHtml,
  escapeHtml,
  openPrintWindow,
  PRINT_NO_PRINT_CSS,
  PRINT_POPUP_BLOCKED_MESSAGE,
} from '../../../../utils/printWindow';

export interface UsePrintReceiptModalProps {
  orderNumber: string;
  /** ISO 8601 del servidor (createdAt); si falta se usa la hora actual al abrir el modal */
  orderCompletedAt?: string | null;
}

export const usePrintReceiptModal = ({
  orderNumber,
  orderCompletedAt,
}: UsePrintReceiptModalProps) => {
  // Helper: discount as % with Bs equivalent, or Bs with % equivalent
  const formatDiscountDisplay = useCallback(
    (discountType: string, discountValue: number, originalPrice: number) =>
      formatPriceAdjustmentDisplay(discountType, discountValue, originalPrice),
    [],
  );

  // Handle print functionality
  const handlePrint = useCallback(() => {
    const printContent = document.getElementById('receipt-content');
    if (!printContent) return;

    const html = `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8"/>
    <title>Comprobante de Venta - ${escapeHtml(orderNumber)}</title>
    <style>
      body { font-family: 'Courier New', monospace; margin: 0; padding: 20px; background: white; }
      .receipt { max-width: 300px; margin: 0 auto; }
      .text-center { text-align: center; }
      .text-right { text-align: right; }
      .border-top { border-top: 1px dashed #000; margin: 10px 0; padding-top: 10px; }
      .font-bold { font-weight: bold; }
      .text-lg { font-size: 18px; }
      .text-sm { font-size: 12px; }
      .mb-2 { margin-bottom: 8px; }
      .mb-4 { margin-bottom: 16px; }
      .flex { display: flex; justify-content: space-between; }
      .w-full { width: 100%; }
      @media print {
        body { margin: 0; padding: 10px; }
      }
      ${PRINT_NO_PRINT_CSS}
    </style>
  </head>
  <body>
    ${printContent.innerHTML}
    ${buildPrintChromeHtml()}
  </body>
</html>`;

    const printWindow = openPrintWindow({
      html,
      size: 'ticket',
      windowName: 'printSalesReceipt',
    });

    if (!printWindow) {
      window.alert(PRINT_POPUP_BLOCKED_MESSAGE);
    }
  }, [orderNumber]);

  const receiptDateTime = useMemo(() => {
    if (orderCompletedAt) {
      return formatDateTime(orderCompletedAt);
    }
    return formatDateTime(new Date());
  }, [orderCompletedAt]);

  return {
    formatDiscountDisplay,
    handlePrint,
    receiptDateTime,
  };
};
